import {useCallback, useEffect, useState} from 'react';
import styles from './Profile.module.css';
import Button from '../../Button/Button';
import {
	JWT_STORAGE_KEY,
	mcpKeysUrl,
	mcpMyKeyUrl,
	revokeMcpKeyUrl,
	rotateMcpKeyUrl
} from '../../../helpers/constants';
import {useNavigate} from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';
import useApi from '../../../hooks/useApi.hook';

const formatDate = (value) =>
	value ? new Date(value).toLocaleString('ru-RU') : null;

export function Profile() {
	const navigate = useNavigate();
	const api = useApi();

	const jwt = localStorage.getItem(JWT_STORAGE_KEY);
	const decoded = jwtDecode(jwt);

	const [key, setKey] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(undefined);
	const [copied, setCopied] = useState(false);
	const [confirming, setConfirming] = useState(null); // null | 'rotate' | 'revoke'
	// Ключ выпускается только на чтение, пока право записи не запрошено явно.
	const [writeScope, setWriteScope] = useState(false);

	const isSuperuser = Boolean(decoded?.is_superuser);

	const handleError = (e) =>
		setError(
			e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка'
		);

	const loadKey = useCallback(async () => {
		try {
			const {data} = await api.get(mcpMyKeyUrl);
			setKey(data);
		} catch (e) {
			handleError(e);
		} finally {
			setLoading(false);
		}
	}, [api]);

	useEffect(() => {
		if (!isSuperuser) {
			setLoading(false);
			return;
		}
		loadKey();
	}, [isSuperuser, loadKey]);

	const acceptNewKey = (data) => {
		setKey(data);
		setCopied(false);
		setConfirming(null);
	};

	const createKey = async () => {
		setError(undefined);
		try {
			const {data} = await api.post(mcpKeysUrl, {
				name: 'MCP клиент',
				scope: writeScope ? 'write' : 'read'
			});
			acceptNewKey(data);
		} catch (e) {
			handleError(e);
		}
	};

	const rotateKey = async () => {
		setError(undefined);
		try {
			const {data} = await api.post(rotateMcpKeyUrl(key.id));
			acceptNewKey(data);
		} catch (e) {
			handleError(e);
		}
	};

	const revokeKey = async () => {
		setError(undefined);
		try {
			await api.delete(revokeMcpKeyUrl(key.id));
			setKey(null);
			setConfirming(null);
		} catch (e) {
			handleError(e);
		}
	};

	const copyKey = async () => {
		try {
			await navigator.clipboard.writeText(key.key);
			setCopied(true);
		} catch (e) {
			setError('Не удалось скопировать — выделите ключ и скопируйте вручную');
		}
	};

	const LogOut = () => {
		localStorage.removeItem(JWT_STORAGE_KEY);
		navigate('/');
	};

	const renderKeyBlock = () => {
		if (loading) {
			return <div className={styles['key_hint']}>Загрузка…</div>;
		}

		if (!key) {
			return (
				<>
					<div className={styles['key_hint']}>
						Ключ не выпущен. Он нужен, чтобы подключить ИИ-клиента к системе.
					</div>
					<label className={styles['key_scope']}>
						<input
							type="checkbox"
							checked={writeScope}
							onChange={(e) => setWriteScope(e.target.checked)}
						/>
						<span>
							Разрешить изменять данные — ИИ сможет заводить, править и
							удалять сотрудников. Без галочки ключ только читает.
						</span>
					</label>
					<div className={styles['key_actions']}>
						<Button onClick={createKey}>Создать ключ</Button>
					</div>
				</>
			);
		}

		if (confirming) {
			const isRotate = confirming === 'rotate';
			return (
				<>
					<div className={styles['key_warning']}>
						{isRotate
							? 'Текущий ключ перестанет работать сразу, ИИ-клиента придётся перенастроить на новый. Права нового ключа останутся прежними. Продолжить?'
							: 'Текущий ключ перестанет работать сразу, новый выписан не будет. Продолжить?'}
					</div>
					<div className={styles['key_actions']}>
						<Button onClick={isRotate ? rotateKey : revokeKey}>
							{isRotate ? 'Да, обновить' : 'Да, отозвать'}
						</Button>
						<Button onClick={() => setConfirming(null)}>Отмена</Button>
					</div>
				</>
			);
		}

		return (
			<>
				<div className={styles['key_value']}>{key.key || key.masked_key}</div>
				{!key.key && (
					<div className={styles['key_hint']}>
						Ключ выпущен до того, как их стали показывать целиком. Обновите его,
						чтобы видеть значение.
					</div>
				)}
				<div
					className={
						key.scope === 'write'
							? styles['key_warning']
							: styles['key_hint']
					}
				>
					{key.scope === 'write'
						? 'Права: чтение и изменение данных'
						: 'Права: только чтение'}
				</div>
				<div className={styles['key_hint']}>
					{key.last_used_at
						? `Последнее использование: ${formatDate(key.last_used_at)}`
						: 'Ещё ни разу не использовался'}
				</div>
				<div className={styles['key_actions']}>
					{key.key && (
						<Button onClick={copyKey}>
							{copied ? 'Скопировано' : 'Скопировать'}
						</Button>
					)}
					<Button onClick={() => setConfirming('rotate')}>Обновить</Button>
					<Button onClick={() => setConfirming('revoke')}>Отозвать</Button>
				</div>
			</>
		);
	};

	return (
		<div className={styles['help']}>
			<div className={styles['content']}>
				<div className={styles['title']}>
					Профиль: {decoded.email}
				</div>

				{isSuperuser && (
					<div className={styles['key_block']}>
						<div className={styles['key_title']}>Ключ для ИИ (MCP)</div>
						{renderKeyBlock()}
					</div>
				)}

				{error && <div className={styles['error']}>{error}</div>}
			</div>
			<div className={styles['button']}>
				<Button onClick={LogOut}>
					Выход
				</Button>
			</div>
		</div>
	);
}
